import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CaseEvent {
  id: string;
  case_id: string;
  event_type_id: string;
  title: string | null;
  event_date: string;
  event_time: string | null;
  deadline_date: string | null;
  location_address: string | null;
  location_details: string | null;
  officer_name: string | null;
  officer_id: string | null;
  result: string | null;
  notes: string | null;
  additional_data: Record<string, any> | null;
  created_at: string | null;
  created_by: string | null;
  client_notified_at: string | null;
  action_required: boolean | null;
  action_completed_at: string | null;
  event_type?: {
    id: string;
    name: string;
    code: string;
    category: string;
    icon: string | null;
    color: string | null;
  };
  creator?: {
    display_name: string;
    first_name: string | null;
    last_name: string | null;
  };
}

export interface EventType {
  id: string;
  name: string;
  code: string;
  category: string;
  requires_location: boolean;
  requires_officer: boolean;
  requires_result: boolean;
  requires_deadline: boolean;
  icon: string | null;
  color: string | null;
  is_active: boolean;
}

export function useCaseEvents(caseId: string | null) {
  const queryClient = useQueryClient();

  const { data: events = [], isLoading, error, refetch } = useQuery({
    queryKey: ['case-events', caseId],
    queryFn: async () => {
      if (!caseId) return [];
      
      const { data, error } = await supabase
        .from('case_events')
        .select(`
          *,
          event_type:event_types(id, name, code, category, icon, color)
        `)
        .eq('case_id', caseId)
        .order('event_date', { ascending: false });

      if (error) throw error;
      
      // Fetch creators separately if needed
      const eventsWithCreator = await Promise.all(
        (data || []).map(async (event) => {
          if (event.created_by) {
            const { data: creator } = await supabase
              .from('app_users')
              .select('display_name, first_name, last_name')
              .eq('auth_user_id', event.created_by)
              .single();
            return { ...event, creator } as CaseEvent;
          }
          return event as CaseEvent;
        })
      );
      
      return eventsWithCreator;
    },
    enabled: !!caseId,
  });

  const { data: eventTypes = [] } = useQuery({
    queryKey: ['event-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_types')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      return data as EventType[];
    },
  });

  const createEventMutation = useMutation({
    mutationFn: async (eventData: {
      event_type_id: string;
      event_date: string;
      title?: string;
      event_time?: string;
      deadline_date?: string;
      location_address?: string;
      location_details?: string;
      officer_name?: string;
      result?: string;
      notes?: string;
    }) => {
      const { data: currentUser } = await supabase.auth.getUser();

      const { data, error } = await supabase
        .from('case_events')
        .insert({
          case_id: caseId!,
          event_type_id: eventData.event_type_id,
          event_date: eventData.event_date,
          title: eventData.title || null,
          event_time: eventData.event_time || null,
          deadline_date: eventData.deadline_date || null,
          location_address: eventData.location_address || null,
          location_details: eventData.location_details || null,
          officer_name: eventData.officer_name || null,
          result: eventData.result || null,
          notes: eventData.notes || null,
          created_by: currentUser.user?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-events', caseId] });
    },
  });

  const updateEventMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CaseEvent> }) => {
      const { data, error } = await supabase
        .from('case_events')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-events', caseId] });
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('case_events')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['case-events', caseId] });
    },
  });

  // Get next biometrics event
  const nextBiometrics = events.find(e => 
    e.event_type?.code === 'biometrics' && 
    new Date(e.event_date) >= new Date()
  );

  return {
    events,
    eventTypes,
    isLoading,
    error,
    refetch,
    createEvent: createEventMutation.mutateAsync,
    updateEvent: updateEventMutation.mutateAsync,
    deleteEvent: deleteEventMutation.mutateAsync,
    isCreating: createEventMutation.isPending,
    isUpdating: updateEventMutation.isPending,
    isDeleting: deleteEventMutation.isPending,
    nextBiometrics,
  };
}
